import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Grid, Skeleton } from '@mui/material';
import {
  FitnessCenter,
  CalendarToday,
  LocalFireDepartment,
  Timer,
  TrackChanges,
  Speed
} from '@mui/icons-material';
import StatCard from 'components/gamification/StatCard';
import PlayTopMusclesCard from 'components/play/rank/PlayTopMusclesCard';
import { CLIENT_PLAY_SUMMARY } from 'api/graphql/playCoach';
import { formatDuration } from 'components/play/workoutEstimate';

const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '—';
  }
};

/**
 * OTRIX Play (coach) — métricas de UN cliente Play: entrenamientos, adherencia, racha,
 * tiempo, % promedio y distribución muscular. Solo lectura, dark+light por tokens.
 */
function PlayClientMetricsPanel({ clientId }) {
  const { t } = useTranslation(['play']);
  const { data, loading } = useQuery(CLIENT_PLAY_SUMMARY, {
    variables: { user_id: clientId },
    fetchPolicy: 'cache-and-network'
  });

  const summary = data?.clientPlaySummary || null;

  if (loading && !summary) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!summary) return null;

  const tiles = [
    {
      icon: FitnessCenter,
      value: summary.workouts_total,
      label: t('coach.workoutsTotal', { ns: 'play', defaultValue: 'Entrenamientos totales' })
    },
    {
      icon: CalendarToday,
      value: summary.workouts_this_month,
      label: t('coach.workoutsMonth', { ns: 'play', defaultValue: 'Este mes' })
    },
    {
      icon: TrackChanges,
      value: `${summary.adherence_percent}%`,
      label: t('coach.adherence', { ns: 'play', defaultValue: 'Adherencia (4 semanas)' })
    },
    {
      icon: LocalFireDepartment,
      value: t('perfSummary.days', { ns: 'play', count: summary.current_streak_days, defaultValue: '{{count}} días' }),
      label: t('perfSummary.streak', { ns: 'play', defaultValue: 'Racha actual' })
    },
    {
      icon: Timer,
      value: formatDuration(summary.total_seconds),
      label: t('perfSummary.totalTime', { ns: 'play', defaultValue: 'Tiempo total' })
    },
    {
      icon: Speed,
      value: summary.avg_completion_percent !== null ? `${summary.avg_completion_percent}%` : '—',
      label: t('coach.avgCompletion', { ns: 'play', defaultValue: 'Cumplimiento promedio' })
    }
  ];

  return (
    <Grid container spacing={2}>
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <Grid item xs={12} sm={6} md={4} key={tile.label}>
            <StatCard title={tile.label} value={tile.value} icon={<Icon sx={{ fontSize: 22 }} />} />
          </Grid>
        );
      })}
      <Grid item xs={12} md={8}>
        <PlayTopMusclesCard distribution={summary.muscle_distribution} />
      </Grid>
      <Grid item xs={12} md={4}>
        <StatCard
          title={t('perf.lastActivity', { ns: 'play', defaultValue: 'Última actividad' })}
          value={summary.last_workout_at ? fmtDate(summary.last_workout_at) : '—'}
          icon={<CalendarToday sx={{ fontSize: 22 }} />}
        />
      </Grid>
    </Grid>
  );
}

PlayClientMetricsPanel.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default PlayClientMetricsPanel;
